import { Injectable, OnDestroy, signal } from '@angular/core';

export type ThemePreference = 'light' | 'dark' | 'system';
export type EffectiveTheme = 'light' | 'dark';

@Injectable({ providedIn: 'root' })
export class ThemeService implements OnDestroy {
  private readonly storageKey = 'fi-theme';
  private readonly themeRevision = signal(0);
  private readonly preferenceState = signal<ThemePreference>(this.readPreference());
  private readonly effectiveThemeState = signal<EffectiveTheme>('light');
  private readonly mediaQuery = this.createMediaQuery();
  private readonly systemThemeChangeListener = (event: MediaQueryListEvent): void => {
    if (this.preference() === 'system') {
      this.applyEffectiveTheme(event.matches ? 'dark' : 'light');
    }
  };

  readonly revision = this.themeRevision.asReadonly();
  readonly preference = this.preferenceState.asReadonly();
  readonly effectiveTheme = this.effectiveThemeState.asReadonly();

  constructor() {
    this.mediaQuery?.addEventListener('change', this.systemThemeChangeListener);
    this.applyEffectiveTheme(this.resolveEffectiveTheme(this.preference()));
  }

  get isDark(): boolean {
    return this.effectiveTheme() === 'dark';
  }

  toggle(): void {
    const nextPreference: Record<ThemePreference, ThemePreference> = {
      light: 'dark',
      dark: 'system',
      system: 'light',
    };
    this.set(nextPreference[this.preference()]);
  }

  set(theme: ThemePreference): void {
    if (this.preference() === theme) {
      return;
    }

    this.preferenceState.set(theme);
    this.writePreference(theme);
    this.applyEffectiveTheme(this.resolveEffectiveTheme(theme));
  }

  getTheme(): EffectiveTheme {
    return this.effectiveTheme();
  }

  ngOnDestroy(): void {
    this.mediaQuery?.removeEventListener('change', this.systemThemeChangeListener);
  }

  private readPreference(): ThemePreference {
    const stored = this.getStorage()?.getItem(this.storageKey);
    return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : 'system';
  }

  private writePreference(preference: ThemePreference): void {
    this.getStorage()?.setItem(this.storageKey, preference);
  }

  private resolveEffectiveTheme(preference: ThemePreference): EffectiveTheme {
    if (preference === 'system') {
      return this.mediaQuery?.matches ? 'dark' : 'light';
    }

    return preference;
  }

  private applyEffectiveTheme(theme: EffectiveTheme): void {
    const root = typeof document === 'undefined' ? undefined : document.documentElement;
    root?.classList.toggle('dark', theme === 'dark');
    root?.style.setProperty('color-scheme', theme);

    if (this.effectiveTheme() !== theme) {
      this.effectiveThemeState.set(theme);
      this.themeRevision.update((value) => value + 1);
    }
  }

  private createMediaQuery(): MediaQueryList | undefined {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }

    return window.matchMedia('(prefers-color-scheme: dark)');
  }

  private getStorage(): Storage | undefined {
    return typeof localStorage === 'undefined' ? undefined : localStorage;
  }
}
